/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { MultiValue } from 'react-select';
import { useState, useMemo } from 'react';
import { useTranslateDocument } from '@utils/api/documents/use-translate-document.tsx';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import cn from 'classnames';
import { DocumentTranslationLanguage, Option } from '@utils/types/shared.ts';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useSharedDocumentFinished } from '@utils/api/magic-links/use-finish-shared-document.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import {
  Languages,
  CheckCircle2,
  Upload,
  FileText,
  FileCode,
  FileVideo,
  Subtitles
} from 'lucide-react';

import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { getDocumentRaw } from '@utils/api/documents/use-get-document-raw.tsx';
import {
  getDocumentDirectUrl,
  triggerDownloadFromUrl
} from '@utils/api/documents/use-get-document-direct.tsx';
import { parse } from '@utils/extensions/content-disposition.extension.ts';
import saveAs from 'file-saver';
import { formatOptions } from '@utils/constants/download-options.ts';
import { useSwallowDocumentFinished } from '@utils/helpers/document-finished.ts';

type Props = {
  allowExport: boolean;
  backendReady: boolean;
};

export const SharedCaptionExportDocument = ({
  allowExport,
  backendReady
}: Props) => {
  const { t } = useTranslation();
  const { session, documentResult, token } = useSharedCaption();

  // Prevent error toasts for handled fallback scenarios
  useSwallowDocumentFinished();

  const [modal, setModal] = useState<'' | 'download' | 'translate'>('');
  const [filenameInput, setFilenameInput] = useState(session?.filename || '');
  const [selectedLanguages, setSelectedLanguages] = useState<Option[]>([]);
  const { mutate: translateDoc, isLoading: translationIsLoading } =
    useTranslateDocument();
  const { getColour } = useThemeContext();
  const sharedDocumentFinishedMutation = useSharedDocumentFinished();

  // Export state
  const [includeSpeakers, setIncludeSpeakers] = useState(true);
  const [includeTimestamps] = useState(true);
  const [exporting, setExporting] = useState(false);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        topNavContainer: {
          borderBottom: `1px solid ${getColour('border')}`
        },
        topNavContainerColor: {
          backgroundColor: getColour('secondLayer'),
          border: `1px solid ${getColour('border')}`
        },
        iconsColor: {
          color: getColour('icon')
        },
        userText: {
          color: getColour('normalText')
        }
      }),
    [getColour]
  );

  const options: Option[] = Object.keys(DocumentTranslationLanguage).map(
    key =>
      ({
        label: key,
        value:
          DocumentTranslationLanguage[
            key as keyof typeof DocumentTranslationLanguage
          ]
      }) as Option
  );

  const getDisabledTooltip = () => {
    if (!backendReady) {
      return t('export-feature-coming-soon') || 'Export feature coming soon';
    }
    if (!allowExport) {
      return (
        t('export-disabled-by-organization') ||
        'Export is disabled by the organization administrator'
      );
    }
    return undefined;
  };

  const handleTranslate = () => {
    if (!documentResult) return;

    const toastId = toast.loading(t('translating-document'));

    translateDoc(
      {
        sessionId: session?.session_id as string,
        documentId: documentResult?.result.id as string,
        languages: selectedLanguages.map(lang => lang.value.toUpperCase()),
        filename: filenameInput
      },
      {
        onSuccess: async data => {
          toast.dismiss(toastId);
          setModal('');

          const shouldAutoDownload =
            (documentResult?.result.use_plain_document &&
              !documentResult?.result.plain_document_changed) ||
            false;

          if (shouldAutoDownload && data?.json && Array.isArray(data.json)) {
            for (const doc of data.json) {
              if (!doc.id || !session?.session_id) continue;
              try {
                const resp = await getDocumentDirectUrl({
                  sessionId: session.session_id,
                  documentId: doc.id,
                  format: 'docx'
                });
                await triggerDownloadFromUrl(
                  resp.result.download_url,
                  resp.result.filename,
                  resp.result.content_type
                );
              } catch {
                try {
                  const resp = await getDocumentRaw({
                    sessionId: session.session_id,
                    documentId: doc.id,
                    format: 'docx'
                  });
                  const disposition = parse(
                    resp.headers['content-disposition'] || ''
                  );
                  const fname =
                    disposition?.parameters?.filename ||
                    doc.filename ||
                    'translated.docx';
                  saveAs(resp.data, fname);
                } catch {
                  toast.error(t('error-downloading-document'));
                }
              }
            }
          }
        },
        onError: () => {
          toast.dismiss(toastId);
        }
      }
    );
  };

  const handleFinishDocument = async () => {
    try {
      await sharedDocumentFinishedMutation.mutateAsync({
        sessionId: session?.session_id as string,
        documentId: documentResult?.result.id as string
      });
      toast.success(t('document-finished-notification'));
    } catch (error) {
      const err = error as AxiosError<BaseError>;
      const data = err.response?.data;
      const fallbackMessage = t('error-finishing-document');

      let errorMessage = fallbackMessage;

      if (data?.error_description) {
        errorMessage = data.error_description;
      } else if (data?.detail) {
        if (Array.isArray(data.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errorMessage = (data.detail as any[])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((e: any) => e.msg)
            .join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      } else if (data?.error) {
        errorMessage = data.error;
      } else if (data?.message) {
        errorMessage = data.message;
      }

      toast.error(errorMessage);
    }
  };

  const handleMediaDownload = () => {
    if (!allowExport) {
      toast.error(
        t('export-not-allowed-organization') ||
          'Export functionality is not enabled for this document'
      );
      return;
    }

    if (!session?.media_url) {
      toast.error(t('error-downloading-media'));
      return;
    }

    const linkEl = document.createElement('a');
    linkEl.href = session.media_url;
    linkEl.setAttribute('download', session.filename || 'media-file');
    document.body.appendChild(linkEl);
    linkEl.click();
    document.body.removeChild(linkEl);
  };

  const handleExport = async (format: string) => {
    if (!allowExport) {
      toast.error(
        t('export-not-allowed-organization') ||
          'Export functionality is not enabled for this document'
      );
      return;
    }

    if (!documentResult || !session?.session_id) {
      toast.error(t('error-downloading-document'));
      return;
    }

    toast.loading(t('preparing-download'), { id: 'exporting-document' });

    try {
      if (format === 'media') {
        handleMediaDownload();
        setModal('');
        toast.success(t('download-ready'), { id: 'exporting-document' });
      } else {
        // Formats that typically have pre-generated files and support direct download
        const supportsDirectDownload = ['docx', 'pdf'].includes(format);

        // ✅ COST OPTIMIZATION: Use direct download from Azure Blob Storage for supported formats
        if (supportsDirectDownload) {
          try {
            const response = await getDocumentDirectUrl({
              sessionId: session.session_id as string,
              documentId: documentResult.result.id as string,
              format,
              without_timestamps: !includeTimestamps,
              without_speakers: !includeSpeakers,
              token,
              isShared: true
            });

            // Trigger download directly from Azure Blobs (bypasses load balancer)
            await triggerDownloadFromUrl(
              response.result.download_url,
              response.result.filename,
              response.result.content_type
            );
            setModal('');
            toast.success(t('download-ready'), { id: 'exporting-document' });
            return; // Success, exit early
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (directError: any) {
            // If direct download not supported, fall through to streaming method
            if (
              directError?.response?.status !== 400 ||
              directError?.response?.data?.error !==
                'direct-download-not-supported'
            ) {
              // Re-throw non-fallback errors
              throw directError;
            }
            // Otherwise continue to streaming fallback below
          }
        }

        // Use streaming method for formats without pre-generated files
        // or as fallback when direct download not supported
        const response = await getDocumentRaw({
          sessionId: session.session_id as string,
          documentId: documentResult.result.id as string,
          format,
          without_timestamps: !includeTimestamps,
          without_speakers: !includeSpeakers,
          token,
          isShared: true
        });

        const disposition = parse(
          response.headers['content-disposition'] || ''
        );
        const filename =
          disposition?.parameters?.filename || `download.${format}`;
        saveAs(response.data, filename);
        setModal('');
        toast.success(t('download-ready'), { id: 'exporting-document' });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.dismiss('exporting-document');
      if (error?.response?.status === 404) {
        toast.error(t('document-not-found'));
      } else {
        toast.error(t('error-downloading-document'));
      }
    }
  };

  return (
    <div className={cn('flex justify-end gap-1 sm:gap-2 items-center')}>
      <div className="flex items-center gap-1 flex-wrap">
        <Button
          size="sm"
          variant="secondary"
          icon={<Languages size={12} />}
          label={t('translate')}
          onClick={() => setModal('translate')}
          className="max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Upload size={12} />}
          label={t('export')}
          onClick={() => setModal('download')}
          disabled={!allowExport}
          title={getDisabledTooltip()}
          className="max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<CheckCircle2 size={12} />}
          label={t('finish')}
          onClick={handleFinishDocument}
          className="max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
        />
      </div>

      {/* Translate modal */}
      <Modal
        isOpen={modal === 'translate'}
        onClose={() => setModal('')}
        size="medium"
        title={t('translate-document-to-language')}
        caption=""
        footerActions={[
          <Button
            onClick={() => setModal('')}
            label={t('cancel')}
            size="small"
            variant="smoke"
            key="cancel-translate"
          />,
          <Button
            onClick={handleTranslate}
            label={t('translate')}
            size="small"
            variant="primary"
            loading={translationIsLoading}
            disabled={translationIsLoading}
            key="confirm-translate"
          />
        ]}
      >
        <div className="mb-4 flex gap-2 items-center text-sm">
          <label
            htmlFor="translationInput"
            className={cn(
              'block text-gray-700 col-span-2 min-w-32',
              css(styles.userText)
            )}
          >
            {t('translation-name')}
          </label>
          <Input
            id="translationInput"
            placeholder={session?.filename}
            value={filenameInput}
            onChange={e => setFilenameInput(e.target.value)}
            className={cn('col-span-3')}
          />
        </div>
        <div className="mb-4 flex gap-2 items-center text-sm">
          <label
            htmlFor="languageSelect"
            className={cn(
              'block text-gray-700 max-w-fit col-span-2 min-w-32',
              css(styles.userText)
            )}
          >
            {t('translate-to')}
          </label>
          <SelectInput
            id="languageSelect"
            options={options}
            value={selectedLanguages}
            name="type"
            onChange={(selectedOptions: MultiValue<Option>) => {
              setSelectedLanguages(selectedOptions as Option[]);
            }}
            className="min-w-full"
            isMulti
          />
        </div>
      </Modal>

      {/* Download modal */}
      <Modal
        isOpen={modal === 'download'}
        onClose={() => setModal('')}
        size="medium"
        title={t('export-subtitles')}
        caption={t('export-subtitles-description')}
        footerActions={[
          <Button
            onClick={() => setModal('')}
            label={t('cancel')}
            size="small"
            variant="smoke"
            key="cancel-download"
          />
        ]}
      >
        <div className="space-y-6">
          {/* Content Options Section */}
          <section className="px-4 py-2">
            <div className="flex flex-col gap-1">
              <div>
                <h3
                  className="text-sm font-semibold"
                  style={{ color: getColour('normalText') }}
                >
                  {t('content-options')}
                </h3>
              </div>
              <div className="flex items-center justify-between py-1">
                <Checkbox
                  id="include_speakers"
                  name="include_speakers"
                  label={t('include-speakers')}
                  checked={includeSpeakers}
                  onChange={e => setIncludeSpeakers(e.target.checked)}
                  variant="primary"
                  wrapperClass="w-full flex items-center justify-between cursor-pointer select-none"
                  labelClass="text-sm font-medium"
                />
              </div>
            </div>
          </section>

          {/* Export Options Section */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('export-options')}
              </h3>
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('pick-subtitle-format')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {formatOptions
                .filter(
                  option => option.key !== 'media' || !!session?.media_url
                )
                .map(option => {
                  const IconComponent =
                    option.key === 'docx'
                      ? FileText
                      : option.key === 'html'
                        ? FileCode
                        : option.key === 'media'
                          ? FileVideo
                          : Subtitles;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={async () => {
                        setExporting(true);
                        await handleExport(option.key);
                        setExporting(false);
                      }}
                      disabled={exporting}
                      className="flex h-full w-full items-start gap-3 rounded-2xl border px-3 py-2.5 text-left text-sm shadow-sm hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60"
                      style={{
                        borderColor: getColour('border'),
                        backgroundColor: getColour('secondLayer')
                      }}
                    >
                      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-primary/5 text-primary">
                        <IconComponent className="h-4 w-4" />
                      </span>
                      <span className="flex flex-col gap-0.5">
                        <span
                          className="font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {t(option.labelKey) || option.name}
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t(option.descriptionKey)}
                        </span>
                      </span>
                    </button>
                  );
                })}
            </div>
          </section>
        </div>
      </Modal>
    </div>
  );
};
